<template>
  <DSDialog
    :open="isOpen"
    @update:open="handleUpdateOpen"
  >
    <DSDialogContent class="gap-0">
      <DSDialogHeader class="border-b border-border">
        <div class="flex items-center gap-2">
          <DSButton
            v-if="onBack"
            variant="ghost"
            size="icon-sm"
            class="-ml-2 shrink-0"
            aria-label="Back to more options"
            @click="handleBack"
          >
            <ArrowLeft class="size-4" />
          </DSButton>
          <DSDialogTitle class="text-xl">
            {{ title }}
          </DSDialogTitle>
        </div>
        <DSDialogDescription>
          Export a .ofx or .qfx file from your institution, then import it here.
        </DSDialogDescription>
      </DSDialogHeader>

      <form
        class="flex flex-col gap-5 min-w-0 px-6 py-5"
        @submit.prevent="handleSubmit"
      >
        <DSFileField
          v-model="file"
          :disabled="loading"
          accept=".qfx,.ofx"
          label="Drag your statement here"
          data-refid="connectorsImportFile"
        />
        <div class="flex justify-end gap-2.5">
          <DSButton
            v-if="!file"
            variant="secondary"
            @click="handleCancel"
          >
            Cancel
          </DSButton>
          <DSButton
            v-else
            type="submit"
            :disabled="loading"
            data-refid="connectorsImportButton"
          >
            Import
          </DSButton>
        </div>
      </form>
    </DSDialogContent>
  </DSDialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { LinkedDataManualLoader, ModalClass } from '@types'

import { handleCatchedError } from '@/helpers/common'

import { useNotifications } from '@/plugins/notification'

import { useFilesStore } from '@/store/files'
import { useLinkedDataStore } from '@/store/linkedData'
import { useTasksStore } from '@/store/tasks'

import { ArrowLeft } from '@lucide/vue'

import {
  DSButton,
  DSDialog,
  DSDialogContent,
  DSDialogDescription,
  DSDialogHeader,
  DSDialogTitle,
  DSFileField,
} from '@ds'

type Props = {
  modal: ModalClass<any>
  isImport?: boolean
  /**
   * Return to whatever opened this. A prop rather than an emit so the template
   * can ask whether a previous step exists — this dialog is also opened
   * standalone from ConnectorActions, where there is nothing to go back to.
   */
  onBack?: () => void
}

type Emits = {
  cancel: []
}

const { modal, isImport, onBack } = defineProps<Props>()
const emit = defineEmits<Emits>()

const filesStore = useFilesStore()
const linkedDataStore = useLinkedDataStore()
const tasksStore = useTasksStore()

const file = ref<File>()
const loading = ref(false)

const isOpen = computed(() => modal.isOpened)

const title = computed(() => (isImport ? 'Import data' : 'Use OFX connector'))

const handleClose = () => {
  modal.close()
}

const handleUpdateOpen = (value: boolean) => {
  if (!value) handleCancel()
}

const handleCancel = () => {
  handleClose()
  emit('cancel')
}

const handleBack = () => {
  handleClose()
  onBack?.()
}

/*
 * Upload flow, unchanged from the legacy AddAccountForm: request a signed URL,
 * post the file to it, then hand the resulting key to linkedDataStore.store,
 * which returns a task the tasks store picks up. The dialog closes as soon as
 * the upload starts and the progress notification carries it from there —
 * these files are large enough that holding the dialog open would just block
 * the user.
 */
const handleSubmit = async () => {
  const { progress, remove, error } = useNotifications()

  let progressId
  try {
    if (!file.value) return

    loading.value = true
    handleClose()

    progressId = await progress({
      message: 'Uploading account file to server',
      description: 'This may take several minutes',
    })

    const res = await filesStore.fetchUploadURL({ type: 'manual_sync' })

    const payload = new FormData()
    if (res.fields) {
      Object.entries(res.fields).forEach(([key, value]) => {
        payload.append(key, value)
      })
    }
    payload.append('file', file.value)

    const uploadRes = await filesStore.uploadFile({ url: res.url, payload })

    if (!uploadRes || !res.fields.key) {
      await remove(progressId)
      await error({
        message: 'Error uploading account file to server',
        description:
          'Try to import the account again. If the error repeats, contact support',
      })
      return
    }

    const task = await linkedDataStore.store(
      res.fields.key,
      LinkedDataManualLoader.OFX,
    )
    tasksStore.commit([task])
    await remove(progressId)
    loading.value = false
  } catch (e) {
    handleCatchedError(e as string)
    progressId && (await remove(progressId))
    await error({
      message: 'Failed to upload file',
      description:
        'Try to import the account again. If the error repeats, contact support',
    })
    loading.value = false
  }
}
</script>
